/**
 * The plans shown on the pricing page and in Settings. Prices include VAT, in euros.
 * Storage is the only limit on Free; AI is unlimited with your own key on every plan.
 */
export const PLAN_IDS = ["free", "storage", "researcher", "researcher_plus", "lab"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  /** € per month when paid monthly (null: yearly only or free). */
  monthly: number | null;
  /** € per year when paid yearly. */
  yearly: number | null;
  /** Files you can store, in GB (per person for Lab). */
  storageGb: number;
  /** Included AI (on the server's key): actions a day, or credits a month (1 credit ≈ a small action, 4 ≈ Go in depth). */
  aiPerDay?: number;
  aiCreditsPerMonth?: number;
  /** An address to forward emails into Correspondence. */
  forwardingAddress: boolean;
  /** Reports and manuscripts sent to Overleaf, GitHub or GitLab with Git ("Open in Overleaf" is on every plan). */
  gitSync: boolean;
  /** Project web pages a person can have at once (null: no limit). Password and expiry are on every plan. */
  webPages: number | null;
  /** In plain words. */
  features: string[];
  highlight?: boolean;
  perPerson?: boolean;
}

export const PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    tagline: "Everything, to get going.",
    monthly: 0,
    yearly: 0,
    storageGb: 1,
    aiPerDay: 3,
    forwardingAddress: false,
    gitSync: false,
    webPages: 1,
    features: [
      "1 GB for your files (about 450 papers)",
      "All features: notes, papers, writing, emails, Zotero & Mendeley",
      "Unlimited AI with your own key",
      "3 AI actions a day included, no key needed",
      "A weekly briefing by email or Telegram, with reference and code checks",
      "A web page of a project, with a password and an end date if you want",
      "Locked (encrypted) notebooks",
    ],
  },
  {
    id: "storage",
    name: "Storage",
    tagline: "For a bigger library.",
    monthly: 4,
    yearly: 36,
    storageGb: 20,
    aiPerDay: 3,
    forwardingAddress: false,
    gitSync: false,
    webPages: 1,
    features: ["20 GB for your files (about 9,000 papers)", "Everything in Free"],
  },
  {
    id: "researcher",
    name: "Researcher",
    tagline: "AI included. No key needed.",
    monthly: 9,
    yearly: 84,
    storageGb: 20,
    aiCreditsPerMonth: 80,
    forwardingAddress: true,
    gitSync: true,
    webPages: null,
    highlight: true,
    features: [
      "20 GB for your files",
      "AI included: about 20 in-depth paper readings a month, plus everyday help",
      "To write, pick Claude Opus, GPT Sol or GPT Astra each time (bigger models use more credits)",
      "Your own email address to forward emails into BrainLog",
      "Send reports and manuscripts to Overleaf or GitHub with Git",
      "A knowledge base of each project: two lines by AI on every new item",
      "Web pages of your projects: as many as you need",
      "The assistant: new papers every day, papers read in depth, daily briefings by email or Telegram",
      "Everything in Free",
    ],
  },
  {
    id: "researcher_plus",
    name: "Researcher Plus",
    tagline: "For heavy reading and writing.",
    monthly: 19,
    yearly: 180,
    storageGb: 50,
    aiCreditsPerMonth: 250,
    forwardingAddress: true,
    gitSync: true,
    webPages: null,
    features: ["50 GB for your files", "About 3× more AI (around 60 in-depth readings a month)", "Room to write often with the biggest models", "Everything in Researcher"],
  },
  {
    id: "lab",
    name: "Lab",
    tagline: "For research groups, from 3 people.",
    monthly: null,
    yearly: 108,
    storageGb: 20,
    aiCreditsPerMonth: 80,
    forwardingAddress: true,
    gitSync: true,
    webPages: null,
    perPerson: true,
    features: ["Everything in Researcher, for each person", "Storage and AI shared across the group", "One invoice for the group"],
  },
];

/** Researcher for students (university email address), yearly only: over 40% off. */
export const STUDENT_DISCOUNT = { percent: 40, yearly: 48 };

/**
 * Inviting colleagues: when someone joins with a person's link and confirms their email, both get
 * extra AI credits (used after the plan's own AI, on every plan; they do not expire), up to a limit.
 */
export const INVITATION_REWARD = { credits: 25, maxRewarded: 20 };

/**
 * Offer codes (in announcements, or typed when choosing a plan): free months on the first invoice,
 * once per account, until a date, not with the student price. Each becomes two Stripe coupons
 * (billing:setup): 100% off the first monthly invoice, and the same months off the first year.
 */
export interface Offer {
  code: string;
  /** Free months when paying monthly (the first invoice is free). */
  monthlyFreeMonths: 1;
  /** Free months when paying yearly (taken off the first year: 3 = 25%). */
  yearlyFreeMonths: number;
  /** Last day it can be used (YYYY-MM-DD, UTC). */
  until: string;
}

export const OFFERS: Offer[] = [{ code: "NEWBRAINLOG", monthlyFreeMonths: 1, yearlyFreeMonths: 3, until: "2026-11-30" }];

export const findOffer = (code: string | null | undefined) => (code ? OFFERS.find((o) => o.code === code.trim().toUpperCase()) ?? null : null);
export const offerOpen = (o: Offer, now = new Date()) => now.getTime() <= Date.parse(`${o.until}T23:59:59Z`);
/** "1 free month when paying monthly, 3 free months when paying yearly" */
export const offerText = (o: Offer) =>
  `${o.monthlyFreeMonths} free month${o.monthlyFreeMonths === 1 ? "" : "s"} when you pay monthly, ${o.yearlyFreeMonths} free months when you pay yearly`;

/** Plans people can buy themselves (Lab is arranged by email). */
export const SELF_SERVE_PLANS = ["storage", "researcher", "researcher_plus"] as const;
export type SelfServePlan = (typeof SELF_SERVE_PLANS)[number];

/** Actions that write or review a paper: on paid plans the model is picked for each request, and a bigger
 * model uses more credits (in proportion to its price; the least expensive counts the credits below). */
export const WRITING_AI_ACTIONS = ["section_brief", "section_write", "section_edit", "manuscript_review", "revision_fix", "draft_section", "review_draft", "response_to_reviewers"] as const;

/** Credits of the monthly AI allowance an action uses (anything not listed: 1). */
export const AI_ACTION_CREDITS: Record<string, number> = {
  deep_read: 4,
  project_setup: 4,
  compare_papers: 3,
  section_write: 9,
  manuscript_review: 10,
  revision_fix: 3,
  section_edit: 2,
  section_brief: 2,
  review_draft: 3,
  draft_section: 3,
  response_to_reviewers: 3,
  make_slides: 3,
  learn_step: 2,
  grill_step: 2,
  glossary: 2,
};

/** Reading a scanned PDF with AI: one credit for this many pages (at most SCAN_MAX_PAGES at a time). */
export const SCAN_PAGES_PER_CREDIT = 5;
export const SCAN_MAX_PAGES = 80;
export const scanCredits = (pages: number) => Math.max(1, Math.ceil(pages / SCAN_PAGES_PER_CREDIT));

export const planById = (id: string | null | undefined) => PLANS.find((p) => p.id === id) ?? PLANS[0];
